import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Grid, IconButton, InputAdornment, TextField, Toolbar, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';
import { useHistory } from 'react-router';
import { DataAPIContext } from '../api';
import { CREATE } from '../api/dataProvider';
import { ContextStore } from '../store/contextStore';
import { debounce, disableMuiButton, enableMuiButton } from '../utils';
import ExportButton from '../components/ExportButton';
import BulkActionsMenu from './BulkActionsMenu';
import { Close as CloseIcon } from '@mui/icons-material';

const useToolbarStyles = makeStyles((theme) => ({
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
    rightButton: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
}));

export const ItineraryListToolbar = (props) => {
    const createBt = React.useRef(null);
    const history = useHistory();
    const searchQueryRef = React.useRef(null);
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm } = React.useContext(ContextStore);
    // const history = useHistory();
    const classes = useToolbarStyles();
    const btSize = props.isSmallScreen ? 'medium' : 'small';
    const urlParams = new URLSearchParams(window.location.search);


    const handleSearchQueryChange = (ev) => {
        debouncedSearchQueryChangeHandler.cancel();
        doSearch(searchQueryRef.current?.value);
    };

    const onCreateNewItineraryBtClick = (ev) => {
        disableMuiButton(createBt.current);

        (async () => {
            try {
                const res = await dataAPI(CREATE, props.resource, { data: {} });
                const id = +res.data.id;
                if (id > 0) {
                    history.push(`/explore/itineraries/${id}`);
                }
            } finally {
                if (createBt && createBt.current) {
                    enableMuiButton(createBt.current);
                }
            }
        })();
    };

    const onPublishItineraryBtClick = (ev) => props?.onPublishItinerary(props.selectedIds);
    const onUnpublishItineraryBtClick = (ev) => props?.onUnpublishItinerary(props.selectedIds);
    const onChangeDisplaySettingsBtClick = (ev) => props?.onChangeDisplaySettings(props.selectedIds);
    const onDeleteItineraryBtClick = (ev) => props?.onDeleteItinerary(props.selectedIds);

    const debouncedSearchQueryChangeHandler = debounce(handleSearchQueryChange, 300);

    const doSearch = (query) => props.onQueryChange && props.onQueryChange(query);

    const canCreate = hasPerm('itinerary', 'write');
    // CP-7859: CSV/XLSX of the rows on screen (or the ticked ones), plus the
    // XML/JSON document export. Hidden while a reorder is pending, like
    // everything else here.
    const canExport = hasPerm('itinerary', 'export') && !props.newRowOrder;
    const renderExportButton = (hideLabel) => (
        <ExportButton
            resource={props.resource}
            apiFilters={props.apiFilters}
            query={props.query}
            selectedIds={props.selectedIds}
            isAllSelected={false}
            filenameFallback="itineraries"
            documentFormats={['xml', 'json']}
            onDocumentFormat={props.onDocumentExport}
            size={btSize}
            hideLabel={hideLabel}
        />
    );


    return (
        <Toolbar disableGutters={true} sx={{ minHeight: 0 }}>
            <Grid container direction="row">
                <Grid item xs={12} sm={4} container spacing={2} alignItems={'center'} wrap="nowrap">
                    <Grid item xs sm={12}>
                        <TextField
                            variant="filled"
                            label="Search"
                            size="small"
                            onChange={debouncedSearchQueryChangeHandler}
                            inputRef={searchQueryRef}
                            defaultValue={urlParams.get('query')}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Search">
                                            <IconButton aria-label="do search" onClick={handleSearchQueryChange}>
                                                <SearchIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    {/* On a phone Export is icon-only beside the [+], as on
                        Activities and Users (CP-8291, CP-8292); down in the
                        right-hand group it wrapped onto a row of its own. */}
                    {props.isSmallScreen && (canCreate || canExport) && (
                        <Grid item xs="auto" container justifyContent={'flex-end'} wrap="nowrap" gap={1}>
                            {canCreate && (
                                <Tooltip title="New itinerary">
                                    <Button ref={createBt} onClick={onCreateNewItineraryBtClick} variant="contained" color="primary">
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                            )}
                            {canExport && renderExportButton(true)}
                        </Grid>
                    )}
                </Grid>
                <Grid item xs={4} sm={8} container justifyContent="flex-end" alignItems="center">
                    {hasPerm('itinerary', 'write') && props.newRowOrder && Object.keys(props.newRowOrder).length ? (
                        <>
                            <Box mr={2}>
                                {/* Added reset order functionality */}
                                <Button color="secondary" variant="contained" size={btSize} onClick={(ev) => props.onSaveNewOrder(true)}>
                                    X&nbsp;&nbsp;Cancel
                                </Button>
                            </Box>
                            <Box mr={2}>
                                <Button color="primary" variant="contained" size={btSize} onClick={() => props.onSaveNewOrder()}>
                                    <SaveIcon />
                                    &nbsp;Save new order
                                </Button>
                            </Box>
                        </>
                    ) : null}
                    {!hasPerm('itinerary', 'write') || props.isSmallScreen || props.newRowOrder || props.selectedIds?.length > 0 ? null : (
                        <Box direction="row-reverse">
                            <Button ref={createBt} color="primary" variant="contained" size={props.isSmallScreen ? 'normal' : 'small'} onClick={onCreateNewItineraryBtClick}>
                                <AddIcon />
                                &nbsp;{!props.isSmallScreen ? 'New itinerary' : null}
                            </Button>
                        </Box>
                    )}
                    {hasPerm('itinerary', 'write') && props.selectedIds?.length > 1 ? (
                        <>
                            <Tooltip title="Clear selection">
                                <IconButton variant="text" color="default" onClick={props.onResetSelection}>
                                    <CloseIcon className={classes.menuItemIcon}  />
                                </IconButton>
                            </Tooltip>
                            <Typography sx={{ pr: 3 }}>
                                <Typography component="span" sx={{ fontWeight: 600, display: 'inline' }}>
                                    {props.selectedCount}
                                </Typography>
                                &nbsp;items selected
                            </Typography>
                            <BulkActionsMenu
                                isSmallScreen={props.isSmallScreen}
                                onCreateNewItineraryBtClick={onCreateNewItineraryBtClick}
                                onPublishItinerary={onPublishItineraryBtClick}
                                onUnpublishItinerary={onUnpublishItineraryBtClick}
                                onDeleteItinerary={onDeleteItineraryBtClick}
                                onChangeDisplaySettings={onChangeDisplaySettingsBtClick}
                                onDocumentExport={props.onDocumentExport}
                            />
                        </>
                    ) : null}
                    {canExport && !props.isSmallScreen ? (
                        <Box sx={{ ml: 1 }}>
                            {renderExportButton(false)}
                        </Box>
                    ) : null}
                </Grid>
            </Grid>
            {/* {props.isSmallScreen ? (
                <Box position="fixed" right={theme.spacing(3)} bottom={theme.spacing(14)} zIndex={99000}>
                    <Button ref={createBt} onClick={onCreateNewItineraryBtClick}>
                        <Fab color="primary">
                            <AddIcon />
                        </Fab>
                    </Button>
                </Box>
            ) : null} */}
        </Toolbar>
    );
};

export default ItineraryListToolbar;
